import React from "react";
import {
  FormControl,
  FormHelperText,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
  Box,
} from "@mui/material";

/**
 * Reusable Radio Button Group component.
 * @param {string} label - Group label (matches TextInput style).
 * @param {string} name - Input name.
 * @param {any} value - Selected value.
 * @param {function} onChange - Change handler.
 * @param {Array} options - [{ label: 'Option 1', value: '1' }, ...].
 * @param {boolean} row - Horizontal layout (default vertical).
 * @param {string} error - Error message.
 * @param {boolean} required - Show required asterisk.
 * @param {boolean} disabled - Disable all radios.
 */
const RadioButton = ({
  label,
  name,
  value,
  onChange,
  options = [],
  row = false,
  error = "",
  required = false,
  disabled = false,
  ...rest
}) => {
  return (
    <FormControl
      component="fieldset"
      error={Boolean(error)}
      disabled={disabled}
      required={required}
      fullWidth
    >
      {label && (
        <Typography
          component="legend"
          variant="body2"
          fontWeight={600}
          sx={{
            display: "block",
            mb: 0.5,
            color: error ? "error.main" : "text.secondary",
            transition: "color 0.2s ease-in-out",
          }}
        >
          {label}
          {required && (
            <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>
              *
            </Box>
          )}
        </Typography>
      )}

      <RadioGroup
        aria-label={label || name}
        name={name}
        value={value}
        onChange={onChange}
        row={row}
        {...rest}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={
              <Radio
                size="small"
                sx={{
                  color: error ? "error.main" : "primary.main",
                  "&.Mui-checked": {
                    color: "primary.main",
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: "text.primary" }}>
                {option.label}
              </Typography>
            }
            disabled={disabled || option.disabled}
          />
        ))}
      </RadioGroup>

      {error && <FormHelperText sx={{ ml: 0 }}>{error}</FormHelperText>}
    </FormControl>
  );
};

export default RadioButton;
